/**
 * The bar across the top of the ring: what this calendar is called, which year
 * you are looking at, and the handful of things you can do to it.
 *
 * The actions do not all fit on a phone. Rather than let the row scroll — which
 * hid Share, Keys and Account behind an edge with nothing to say they were
 * there — the secondary ones fold into a single "⋯" menu below
 * `NARROW_WIDTH`, and the primary ones stay where they are.
 */
import { button, el, type Child } from './dom';

/** A named action the bar may show as a button or as a row in the ⋯ menu. */
export interface TopBarAction {
  label: string;
  /** A button when given, an anchor when `href` is. */
  onSelect?: () => void;
  href?: string;
  /** Extra classes for the wide-layout button. */
  class?: string;
}

/**
 * The search field, as the bar sees it: an element to place, and the one thing
 * the bar knows that the field does not — whether there is room for it.
 * `ui/search.ts` is the implementation; the bar takes the shape, not the class.
 */
export interface TopBarSearch {
  root: HTMLElement;
  setNarrow?: (narrow: boolean) => void;
}

export interface TopBarOptions {
  name: string;
  year: number;
  /** Owners can rename; viewers see the name as text. */
  editableName?: boolean;
  onRename?: (name: string) => void | Promise<void>;
  onYear: (year: number) => void;
  /** Between the name and the actions: filtering the ring by what you type. */
  search?: TopBarSearch;
  /** Always visible, however narrow the window. */
  actions?: Child[];
  /** Visible as buttons when there is room, in the ⋯ menu when there is not. */
  secondary?: TopBarAction[];
}

/**
 * At or below this viewport width the secondary actions fold into the ⋯ menu.
 * It is the same 720px at which the side panel becomes a bottom sheet, and the
 * width below which the name, the year stepper and five buttons stop fitting on
 * one line.
 */
export const NARROW_WIDTH = 720;

export function isNarrow(width = viewportWidth()): boolean {
  return width <= NARROW_WIDTH;
}

function viewportWidth(): number {
  // jsdom has no matchMedia, and neither does a very old browser; innerWidth is
  // the same question asked in a way everything answers.
  if (typeof window === 'undefined') return NARROW_WIDTH + 1;
  return window.innerWidth || document.documentElement.clientWidth || NARROW_WIDTH + 1;
}

export class TopBar {
  readonly root: HTMLElement;
  private readonly nameInput: HTMLInputElement;
  private readonly yearValue: HTMLElement;
  private readonly actionsEl: HTMLElement;
  private readonly secondary: TopBarAction[];
  private readonly fixedActions: Child[];
  private readonly menu: HTMLElement;
  private readonly moreButton: HTMLButtonElement;
  private readonly search: TopBarSearch | undefined;
  private year: number;
  private committed: string;
  private narrow: boolean | undefined;

  constructor(options: TopBarOptions) {
    this.year = options.year;
    this.committed = options.name;
    this.secondary = options.secondary ?? [];
    this.fixedActions = options.actions ?? [];
    this.search = options.search;

    this.nameInput = el('input', {
      class: 'topbar__name',
      value: options.name,
      'aria-label': 'Calendar name',
      maxlength: '80',
      spellcheck: 'false',
    });
    this.nameInput.disabled = !options.editableName;
    if (options.editableName) {
      const commit = (): void => {
        const next = this.nameInput.value.trim();
        if (!next) {
          this.nameInput.value = this.committed;
          return;
        }
        if (next === this.committed) return;
        this.committed = next;
        void options.onRename?.(next);
      };
      this.nameInput.addEventListener('blur', commit);
      this.nameInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.nameInput.blur();
        }
        if (event.key === 'Escape') {
          this.nameInput.value = this.committed;
          this.nameInput.blur();
        }
      });
    }

    this.yearValue = el('div', {
      class: 'year__value',
      text: String(options.year),
      'aria-live': 'polite',
    });
    const step = (delta: number): void => {
      this.year += delta;
      this.yearValue.textContent = String(this.year);
      options.onYear(this.year);
    };

    this.menu = el('div', { class: 'menu', role: 'menu', hidden: true });
    this.moreButton = el('button', {
      class: 'btn btn--small topbar__more',
      type: 'button',
      'aria-label': 'More actions',
      'aria-haspopup': 'menu',
      'aria-expanded': 'false',
      text: '⋯',
      onClick: (event: Event) => {
        event.stopPropagation();
        this.toggleMenu();
      },
    });

    this.actionsEl = el('div', { class: 'topbar__actions' });

    this.root = el(
      'header',
      { class: 'topbar' },
      this.nameInput,
      el(
        'div',
        { class: 'year' },
        el('button', { type: 'button', 'aria-label': 'Previous year', text: '‹', onClick: () => step(-1) }),
        this.yearValue,
        el('button', { type: 'button', 'aria-label': 'Next year', text: '›', onClick: () => step(1) }),
      ),
      this.search?.root ?? null,
      this.actionsEl,
    );

    this.layout();
    window.addEventListener('resize', this.onResize);
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    document.addEventListener('keydown', this.onKeyDown, true);
  }

  setName(name: string): void {
    this.committed = name;
    if (document.activeElement !== this.nameInput) this.nameInput.value = name;
  }

  setYear(year: number): void {
    this.year = year;
    this.yearValue.textContent = String(year);
  }

  /** True while the ⋯ menu is up. */
  get menuOpen(): boolean {
    return !this.menu.hidden;
  }

  closeMenu(): void {
    if (this.menu.hidden) return;
    this.menu.hidden = true;
    this.moreButton.setAttribute('aria-expanded', 'false');
  }

  openMenu(): void {
    if (!this.menu.hidden) return;
    this.menu.hidden = false;
    this.moreButton.setAttribute('aria-expanded', 'true');
    this.menu.querySelector<HTMLElement>('[role=menuitem]')?.focus();
  }

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    document.removeEventListener('keydown', this.onKeyDown, true);
    this.root.remove();
  }

  /** Re-lay the action row when the window crosses the narrow threshold. */
  private layout(): void {
    const narrow = isNarrow();
    if (narrow === this.narrow) return;
    this.narrow = narrow;
    this.closeMenu();
    // The field folds to an icon at the same width the actions do: below it
    // the name, the year and the buttons are already the whole row.
    this.search?.setNarrow?.(narrow);

    const children: Child[] = [...this.fixedActions];
    if (narrow) {
      this.menu.replaceChildren(...this.secondary.map((action) => this.menuRow(action)));
      if (this.secondary.length) {
        children.push(el('div', { class: 'topbar__menu-wrap' }, this.moreButton, this.menu));
      }
    } else {
      children.push(...this.secondary.map((action) => this.wideButton(action)));
    }
    this.actionsEl.replaceChildren();
    for (const child of children) {
      if (child === null || child === undefined || child === false) continue;
      this.actionsEl.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
    }
  }

  private wideButton(action: TopBarAction): HTMLElement {
    if (action.href !== undefined) {
      return el('a', {
        class: `btn btn--small ${action.class ?? ''}`.trim(),
        href: action.href,
        text: action.label,
      });
    }
    return button(action.label, () => action.onSelect?.(), {
      class: `btn--small ${action.class ?? ''}`.trim(),
    });
  }

  private menuRow(action: TopBarAction): HTMLElement {
    if (action.href !== undefined) {
      const link = el('a', {
        class: 'menu__item',
        role: 'menuitem',
        href: action.href,
        text: action.label,
      });
      link.addEventListener('click', () => this.closeMenu());
      return link;
    }
    return el('button', {
      class: 'menu__item',
      type: 'button',
      role: 'menuitem',
      text: action.label,
      onClick: () => {
        this.closeMenu();
        action.onSelect?.();
      },
    });
  }

  private toggleMenu(): void {
    if (this.menuOpen) this.closeMenu();
    else this.openMenu();
  }

  private readonly onResize = (): void => this.layout();

  private readonly onDocumentPointerDown = (event: Event): void => {
    if (!this.menuOpen) return;
    const target = event.target;
    if (target instanceof Node && (this.menu.contains(target) || this.moreButton.contains(target))) {
      return;
    }
    this.closeMenu();
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.menuOpen) {
      event.stopPropagation();
      this.closeMenu();
      this.moreButton.focus();
    }
  };
}

/** The strip along the bottom: a readout on the left, view controls right. */
export function footerBar(left: Child[], right: Child[]): HTMLElement {
  return el(
    'footer',
    { class: 'footerbar' },
    ...left,
    el('div', { class: 'footerbar__right' }, ...right),
  );
}

/** How tall the footer strip is, for anything that must not sit on top of it. */
export const FOOTER_HEIGHT_VAR = '--footer-h';

/**
 * Publish the footer's height, so the bottom sheet can stop above it.
 *
 * On a phone the panel is a sheet across the whole width, and it was drawn over
 * the footer: the views bar, Save, Revert, Reset view and Reset layout were all
 * under it, and a press on any of them hit the sheet. The sheet now ends where
 * the footer begins, which needs a number CSS cannot work out for itself — the
 * strip is one row or two depending on how many views there are, and it carries
 * the safe-area inset. Measured here and written as a custom property; a
 * `ResizeObserver` keeps it true through a rotation, a new view pill, or the
 * address bar sliding away. Returns the way to stop.
 */
export function trackFooterHeight(
  footer: HTMLElement,
  root: HTMLElement = document.documentElement,
): () => void {
  const measure = (): void => {
    const height = footer.getBoundingClientRect().height;
    root.style.setProperty(FOOTER_HEIGHT_VAR, `${Math.round(height)}px`);
  };
  measure();

  // jsdom has neither, and a footer that never moves is still measured once.
  const observer =
    typeof ResizeObserver === 'function' ? new ResizeObserver(() => measure()) : null;
  observer?.observe(footer);
  const onResize = (): void => measure();
  window.addEventListener?.('resize', onResize);

  return () => {
    observer?.disconnect();
    window.removeEventListener?.('resize', onResize);
    root.style.removeProperty(FOOTER_HEIGHT_VAR);
  };
}

export { button };
